import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import PriceWrapper from 'components/PriceWrapper/PriceWrapper';
import { Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

import BoxSection from '../BoxSection';
import ADDONS_TYPES from '../../consts/addonsTypes';
import Header from '../DietSummary/Header';
import Item from '../DietSummary/Item';
import AdditionalMealTypes from '../DietSummary/AdditionalMealTypes';

const getAddonLabel = addon => `${addon?.addon?.name} × ${addon?.quantity}`;

const useStyles = makeStyles({
  divider: {
    margin: '16px 0',
  },
  pricePerNextDays: {
    marginTop: '16px',
  },
  showSummary: { marginTop: '15px' },
});

const DietSummarySection = ({ diet, dietCost, dietIndex } = {}) => {
  const classes = useStyles();

  const { t } = useTranslation();

  const {
    address,
    dietLength,
    pickUpPoint,
    selectedDays,
    selectedDiet,
    selectedVariant,
    selectedCalorific,
  } = diet || {};

  const dietFilteredWithRows = dietCost?.rows?.filter(
    // eslint-disable-next-line no-shadow
    diet => diet.connectedTo === null
  );

  const addons = dietFilteredWithRows?.[dietIndex]?.addons;

  const priceAdditionalMealTypeBeforeDiscount = dietCost?.rows
    ?.filter(
      el =>
        el?.connectedTo?.['@id'] === dietFilteredWithRows?.[dietIndex]?.['@id']
    )
    .reduce((acc, current) => acc + current?.price?.beforeDiscount, 0);

  const priceAdditionalMealTypeAfterDiscount = dietCost?.rows
    ?.filter(
      el =>
        el?.connectedTo?.['@id'] === dietFilteredWithRows?.[dietIndex]?.['@id']
    )
    .reduce((acc, current) => acc + current?.price?.afterDiscount, 0);

  const priceBeforeDiscount =
    dietFilteredWithRows?.[dietIndex]?.price?.beforeDiscount +
    priceAdditionalMealTypeBeforeDiscount;

  const priceDietPerDayBeforeDiscount =
    dietFilteredWithRows?.[dietIndex]?.price?.priceParts?.DIET / dietLength;

  const priceAfterDiscount =
    dietFilteredWithRows?.[dietIndex]?.price?.afterDiscount +
    priceAdditionalMealTypeAfterDiscount;

  const deliveryBeforeDiscount =
    dietFilteredWithRows?.[dietIndex]?.price?.priceParts?.DELIVERY || 0;
  const deliveryPricePerDayBeforeDiscount = deliveryBeforeDiscount / dietLength;

  const discountParts =
    dietFilteredWithRows?.[dietIndex]?.price?.discountParts || 0;

  const discountPriceAllValue =
    discountParts &&
    Object.keys(discountParts)
      .map(el => discountParts[el])
      .reduce((acc, discount) => acc + discount, 0);

  const priceAllAddonsBeforeDiscount =
    dietFilteredWithRows?.[dietIndex]?.price?.priceParts?.ADDON || 0;

  const priceAllAddonsPerDaysBeforeDiscount = priceAllAddonsBeforeDiscount
    ? priceAllAddonsBeforeDiscount / dietLength
    : 0;

  const priceAddonsFirstDayBeforeDiscount = addons
    ?.map(addon => addon?.quantity * addon?.addon?.clientCost)
    .reduce((acc, addon) => acc + addon, 0);

  const priceAddonsOtherDaysPerDaysBeforeDiscount =
    (priceAllAddonsBeforeDiscount - priceAddonsFirstDayBeforeDiscount) /
    (dietLength - 1);

  const priceEcoContainer =
    dietFilteredWithRows?.[dietIndex]?.price?.priceParts
      ?.OPTION_ECO_CONTAINERS || 0;

  const isDietCostAvailable =
    !isEmpty(dietCost) &&
    !isEmpty(selectedDiet) &&
    !isEmpty(selectedVariant) &&
    !isEmpty(selectedCalorific) &&
    dietLength === selectedDays?.length &&
    (!isEmpty(address) || !isEmpty(pickUpPoint));

  return (
    <BoxSection
      title={t('orders.dietCost')}
      headerRightCornerComponent={
        <div data-cy="new-order_diet-cost_price-wrapper">
          {isDietCostAvailable ? (
            <PriceWrapper
              beforeDiscount={priceBeforeDiscount}
              afterDiscount={priceAfterDiscount}
            />
          ) : null}
        </div>
      }
    >
      <div>
        {isDietCostAvailable &&
          (priceAddonsFirstDayBeforeDiscount > 0 &&
          priceAddonsFirstDayBeforeDiscount !==
            priceAddonsOtherDaysPerDaysBeforeDiscount ? (
            <div>
              <Header
                title={t('orders.pricePerFirstDay')}
                isValidator={priceDietPerDayBeforeDiscount !== ''}
                value={
                  deliveryPricePerDayBeforeDiscount +
                  priceDietPerDayBeforeDiscount +
                  priceAddonsFirstDayBeforeDiscount +
                  priceEcoContainer
                }
              />
              <Item
                title={t('orders.diet')}
                isValidator={priceDietPerDayBeforeDiscount !== ''}
                value={priceDietPerDayBeforeDiscount}
              />
              <Item
                title={t('orders.addonsForFirstDayPriceAfterDiscount')}
                isValidator={priceAddonsFirstDayBeforeDiscount !== ''}
                value={priceAddonsFirstDayBeforeDiscount}
              />
              <ul>
                {addons?.map(addon => (
                  // eslint-disable-next-line react/jsx-key
                  <li>{getAddonLabel(addon)}</li>
                ))}
              </ul>
              <Item
                title={t('orders.delivery')}
                isValidator={deliveryPricePerDayBeforeDiscount !== ''}
                value={deliveryPricePerDayBeforeDiscount}
              />
              <Item
                title={t('orders.ecoContainer')}
                isValidator={priceEcoContainer !== ''}
                value={priceEcoContainer}
              />

              {/* PRICE PER NEXT DAYS */}
              {dietLength > 1 && (
                <div className={classes.pricePerNextDays}>
                  <Header
                    title={t('orders.pricePerNextDays')}
                    isValidator={
                      priceAddonsOtherDaysPerDaysBeforeDiscount !== ''
                    }
                    value={
                      deliveryPricePerDayBeforeDiscount +
                      priceDietPerDayBeforeDiscount +
                      priceAddonsOtherDaysPerDaysBeforeDiscount +
                      priceEcoContainer
                    }
                  />
                  <Item
                    title={t('orders.diet')}
                    isValidator={priceDietPerDayBeforeDiscount !== ''}
                    value={priceDietPerDayBeforeDiscount}
                  />
                  {!!priceAddonsOtherDaysPerDaysBeforeDiscount && (
                    <>
                      <Item
                        title={t(
                          'orders.addonsForAnotherDaysPriceAfterDiscount'
                        )}
                        isValidator={
                          priceAddonsOtherDaysPerDaysBeforeDiscount !== ''
                        }
                        value={priceAddonsOtherDaysPerDaysBeforeDiscount}
                      />
                      <ul>
                        {addons
                          ?.filter(addon => addon.type === ADDONS_TYPES.FOR_BAG)
                          .map(addon => (
                            // eslint-disable-next-line react/jsx-key
                            <li>{getAddonLabel(addon)}</li>
                          ))}
                      </ul>
                    </>
                  )}
                  <Item
                    title={t('orders.delivery')}
                    isValidator={deliveryPricePerDayBeforeDiscount !== ''}
                    value={deliveryPricePerDayBeforeDiscount}
                  />
                  <Item
                    title={t('orders.ecoContainer')}
                    isValidator={priceEcoContainer !== ''}
                    value={priceEcoContainer}
                  />
                </div>
              )}
            </div>
          ) : (
            <div>
              <Header
                title={t('orders.pricePerDay')}
                isValidator={priceDietPerDayBeforeDiscount !== ''}
                value={
                  deliveryPricePerDayBeforeDiscount +
                  priceDietPerDayBeforeDiscount +
                  priceAllAddonsPerDaysBeforeDiscount +
                  priceEcoContainer / dietLength
                }
              />
              <Item
                title={t('orders.diet')}
                isValidator={priceDietPerDayBeforeDiscount !== ''}
                value={priceDietPerDayBeforeDiscount}
              />
              {!!priceAllAddonsPerDaysBeforeDiscount && (
                <>
                  <Item
                    title={t('orders.addonsForAnotherDaysPriceAfterDiscount')}
                    isValidator={priceAllAddonsPerDaysBeforeDiscount !== ''}
                    value={priceAllAddonsPerDaysBeforeDiscount}
                  />

                  <ul>
                    {addons
                      ?.filter(addon => addon.type === ADDONS_TYPES.FOR_BAG)
                      .map(addon => (
                        // eslint-disable-next-line react/jsx-key
                        <li>{getAddonLabel(addon)}</li>
                      ))}
                  </ul>
                </>
              )}
              <Item
                title={t('orders.delivery')}
                isValidator={deliveryPricePerDayBeforeDiscount !== ''}
                value={deliveryPricePerDayBeforeDiscount}
              />
              <Item
                title={t('orders.ecoContainer')}
                isValidator={priceEcoContainer !== ''}
                value={priceEcoContainer}
              />
            </div>
          ))}

        {priceAdditionalMealTypeBeforeDiscount > 0 && (
          <div>
            <Divider className={classes.divider} />
            <AdditionalMealTypes
              value={priceAdditionalMealTypeBeforeDiscount}
            />
          </div>
        )}

        {isDietCostAvailable &&
          Object.values(discountParts || []).length > 0 && (
            <>
              <Divider className={classes.divider} />
              <Header
                title={t('orders.discounts')}
                isValidator={discountPriceAllValue !== ''}
                value={discountPriceAllValue}
                negativeValue={true}
              />
              {Object.keys(discountParts || []).map(discountKey => (
                // eslint-disable-next-line react/jsx-key
                <Item
                  title={t(`basket.priceDetails.discountInfo.${discountKey}`)}
                  isValidator={true}
                  value={discountParts[discountKey]}
                  negativeValue={true}
                />
              ))}
            </>
          )}
        {isDietCostAvailable && (
          <>
            <Divider className={classes.divider} />
            <Header
              title={t('orders.priceAfter')}
              isValidator={priceAfterDiscount}
              value={priceAfterDiscount}
            />
          </>
        )}

        {!isDietCostAvailable && (
          <div>
            <p className={classes.showSummary}>{t('orders.summaryShow')}</p>
          </div>
        )}
      </div>
    </BoxSection>
  );
};

export default DietSummarySection;
